// Define the custom block type locally to avoid circular dependencies
const CustomBlockType = {
  USERS_CRITERIA_BLOCK: 'users_criteria_block' as const,
};

import { createCustomBlock } from '../../../utils/createCustomBlock';
import { t } from '../../../utils/I18nManager';
import { resolvePreviewColor } from '@core/utils';
import { BasicType } from '../../../constants';
import { IBlockData, RecursivePartial } from '../../../typings';
import { BasicBlock } from '../../../components/BasicBlock';
import { merge } from 'lodash';
import * as React from 'react';
import * as Handlebars from 'handlebars';
import { userLayouts } from '../../../data';

interface UserItem {
  id: string;
  name?: string;
  address?: string;
  licenceNumber?: string;
  phone?: string;
  profileImage?: string;
  title?: string; // Realtor or Broker
  email?: string;
}

interface CriteriaConfig {
  userType: string[];
  performanceType: string[];
  timeframe: string;
  minSales?: number;
  maxSales?: number;
  region?: string;
  brokerageId?: string;
}

interface IUsersCriteriaBlockData {
  sourceType: 'criteria';
  criteriaConfig?: CriteriaConfig;
  criteriaResults?: UserItem[];
  maxCount: number;
}

interface IUsersCriteriaBlockAttributes extends Record<string, any> {
  'background-color': string;
  'button-text-color': string;
  'button-color': string;
  'user-name-color': string;
  'user-title-color': string;
  'user-address-color': string;
  'user-phone-color': string;
  'user-licence-color': string;
  'title-font-size': string;
  'button-text': string;
  'show-name': boolean;
  'show-title': boolean;
  'show-address': boolean;
  'show-phone': boolean;
  'show-licence': boolean;
  layout: 'single' | 'double' | 'table';
  padding: string;
  title: string;
}

export type IUsersCriteriaBlock = IBlockData<
  IUsersCriteriaBlockAttributes,
  IUsersCriteriaBlockData
>;

// Helper function to generate merge tags for editor view
function generateUserMergeTags(count: number = 6): any[] {
  const mergeTags = [];
  for (let i = 0; i < count; i++) {
    mergeTags.push({
      id: `user-${i + 1}`,
      name: `{{user.name}}`,
      title: `{{user.title}}`,
      address: `{{user.address}}`,
      phone: `{{user.phone}}`,
      licenceNumber: `{{user.licenceNumber}}`,
      email: `{{user.email}}`,
      profileImage: `https://placehold.co/200x200/e1e1e1/666666?text=Agent+${i + 1}`,
    });
  }
  return mergeTags;
}

// Helper function to prepare user data for handlebars templates
function prepareUser(user: any) {
  return {
    id: user.id,
    name: user.name || '{{user.name}}',
    title: user.title || '{{user.title}}',
    address: user.address || '{{user.address}}',
    phone: user.phone || '{{user.phone}}',
    licenceNumber: user.licenceNumber || '{{user.licenceNumber}}',
    email: user.email || '{{user.email}}',
    profileImage: user.profileImage || '{{user.profileImage}}',
  };
}

// Helper function to pair users for double column layout
function pairUsers(users: any[]): any[][] {
  const pairs = [];
  for (let i = 0; i < users.length; i += 2) {
    const pair = [users[i]];
    if (i + 1 < users.length) {
      pair.push(users[i + 1]);
    }
    pairs.push(pair);
  }
  return pairs;
}

export const UsersCriteriaBlock = createCustomBlock<IUsersCriteriaBlock>({
  get name() {
    return t('Criteria');
  },
  type: CustomBlockType.USERS_CRITERIA_BLOCK,
  validParentType: [
    BasicType.PAGE,
    BasicType.WRAPPER,
    BasicType.SECTION,
    BasicType.COLUMN,
  ],
  create: (payload?: RecursivePartial<IUsersCriteriaBlock>) => {
    const defaultData: IUsersCriteriaBlock = {
      type: CustomBlockType.USERS_CRITERIA_BLOCK,
      data: {
        value: {
          sourceType: 'criteria',
          maxCount: 6,
        },
      },
      attributes: {
        'background-color': '#ffffff',
        'button-text-color': '#ffffff',
        'button-color': '#007bff',
        'user-name-color': '#000000',
        'user-title-color': '#0066cc',
        'user-address-color': '#666666',
        'user-phone-color': '#666666',
        'user-licence-color': '#666666',
        'title-font-size': '16px',
        'button-text': 'Contact Agent',
        'show-name': true,
        'show-title': true,
        'show-address': true,
        'show-phone': true,
        'show-licence': true,
        layout: 'double',
        padding: '20px',
        title: 'Criteria',
      },
      children: [],
    };
    return merge(defaultData, payload);
  },
  render: (params: {
    data: IUsersCriteriaBlock;
    idx?: string | null;
    mode: 'testing' | 'production';
    context?: any;
    dataSource?: { [key: string]: any };
    children?: React.ReactNode;
    keepClassName?: boolean;
    renderPortal?: any;
  }) => {
    const { data } = params;
    const isTest = params.mode === 'testing';
    const selectedLayout = data.attributes?.layout || 'double';

    // Generate CSS classes for editor selectability [[memory:7441590]]
    let cssClasses = 'email-block';
    if (isTest && params.idx) {
      cssClasses = `email-block node-idx-${params.idx} node-type-${data.type}`;
    }

    // Safe data access with defaults
    const maxCount = data.data?.value?.maxCount || 6;
    const criteriaResults = data.data?.value?.criteriaResults || [];

    // Use merge tags in editor view, actual data in production
    const users = isTest ? generateUserMergeTags(maxCount) : criteriaResults;

    if (users.length === 0) {
      return (
        <BasicBlock
          params={params}
          tag='mj-text'
        >
          <div
            className={cssClasses}
            style={{
              textAlign: 'center',
              padding: '20px',
              color: '#666',
              fontSize: '14px',
            }}
          >
            {isTest
              ? 'Users Criteria Block - Configure criteria to display users'
              : 'No users match the criteria'}
          </div>
        </BasicBlock>
      );
    }

    // Find the layout configuration (same as Users block)
    const layoutConfig = userLayouts.find(
      (layoutItem: any) => layoutItem.id === selectedLayout,
    );
    if (!layoutConfig) {
      console.error(`Layout "${selectedLayout}" not found`);
      return (
        <BasicBlock
          params={params}
          tag='mj-text'
        >
          <div>Layout configuration not found</div>
        </BasicBlock>
      );
    }

    // Prepare users with fallback values
    const preparedUsers = users.map(prepareUser);

    // Resolve dynamic brand-colour merge tags to real hex in preview/testing mode;
    // in production the raw tag is returned unchanged for send-time resolution.
    const resolveColor = (key: string, fallback: string) =>
      resolvePreviewColor(data.attributes?.[key], params.mode, params.dataSource) ||
      fallback;

    // Create template context matching Users block structure
    const templateContext = {
      cssClasses,
      blockTitle: data.attributes?.title || 'Criteria',

      // Block attributes mapped to handlebars context (same as Users block)
      backgroundColor: resolveColor('background-color', '#ffffff'),
      userNameColor: resolveColor('user-name-color', '#000000'),
      userTitleColor: resolveColor('user-title-color', '#0066cc'),
      userAddressColor: resolveColor('user-address-color', '#666666'),
      userPhoneColor: resolveColor('user-phone-color', '#666666'),
      userLicenceColor: resolveColor('user-licence-color', '#666666'),
      titleFontSize: data.attributes?.['title-font-size'] || '16px',
      buttonColor: resolveColor('button-color', '#007bff'),
      buttonTextColor: resolveColor('button-text-color', '#ffffff'),
      buttonText: data.attributes?.['button-text'] || 'Contact Agent',
      padding: data.attributes?.['padding'] || '20px',

      // Show/hide flags (same as Users block)
      showName: data.attributes?.['show-name'] !== false,
      showTitle: data.attributes?.['show-title'] !== false,
      showAddress: data.attributes?.['show-address'] !== false,
      showPhone: data.attributes?.['show-phone'] !== false,
      showLicence: data.attributes?.['show-licence'] !== false,

      // Users data (same as Users block)
      users: preparedUsers,

      // For double column layout, we need paired users (same as Users block)
      userPairs: selectedLayout === 'double' ? pairUsers(preparedUsers) : [],
    };

    try {
      // Compile and render the handlebars template (same as Users block)
      const template = Handlebars.compile(layoutConfig.data);
      const htmlContent = template(templateContext);

      // Create proper MJML structure as string (same as Users block)
      const mjmlContent = `
        <mj-section>
          <mj-column>
            <mj-raw>
              ${htmlContent}
            </mj-raw>
          </mj-column>
        </mj-section>
      `;

      return (
        <BasicBlock
          params={params}
          tag='mj-wrapper'
        >
          {mjmlContent}
        </BasicBlock>
      );
    } catch (error) {
      console.error('Error rendering handlebars template:', error);
      console.error('Layout config:', layoutConfig);
      console.error('Template context:', templateContext);
      return (
        <BasicBlock
          params={params}
          tag='mj-text'
        >
          <div>Error rendering template: {(error as Error).message}</div>
        </BasicBlock>
      );
    }
  },
});
